import { useState } from "react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { Eye } from "lucide-react";

export default function SerpPreview() {
  const [form, setForm] = useState({
    title: "SerpNex — Transparent Keyword Rank Tracking",
    description: "Track daily keyword rankings on Google with AI-explained changes and a morning executive summary.",
    url: "https://serpnex.com",
  });
  const [preview, setPreview] = useState(null);

  const check = async () => {
    const r = await api.post("/tools/serp-preview", form);
    setPreview(r.data);
  };

  return (
    <>
      <SEO pageKey="tools_serp" title="Free SERP Preview Tool — SerpNex" description="See how your title tag, URL and meta description render in Google search results. Catch truncation before Google does." canonical="/tools/serp-preview" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="eyebrow">Free tool</div>
        <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold text-[#0A0D14]">SERP Preview</h1>
        <p className="mt-3 text-[#525B6A]">See exactly what Google will show. Live preview + character counts.</p>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="card-crisp p-6 space-y-4">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Title tag <span className="tabular-nums text-[#8A94A6]">{form.title.length}/60</span></label>
              <input data-testid="sp-title" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">URL</label>
              <input data-testid="sp-url" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Meta description <span className="tabular-nums text-[#8A94A6]">{form.description.length}/158</span></label>
              <textarea rows="4" data-testid="sp-desc" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <button onClick={check} data-testid="sp-preview" className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><Eye className="h-4 w-4" /> Preview</button>
          </div>

          <div className="card-crisp p-6" data-testid="sp-result">
            <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium mb-3">Google preview</div>
            <div className="rounded-md border border-[#E2E8F0] p-5 bg-white">
              <div className="text-[13px] text-[#0A0D14]">{form.url}</div>
              <div className="text-[20px] text-[#1a0dab] leading-snug mt-1 hover:underline cursor-pointer" style={{ fontFamily: 'arial, sans-serif' }}>
                {(preview?.title_display || form.title.slice(0, 60) + (form.title.length > 60 ? "…" : ""))}
              </div>
              <div className="text-[14px] text-[#525B6A] mt-2 leading-relaxed" style={{ fontFamily: 'arial, sans-serif' }}>
                {(preview?.description_display || form.description.slice(0, 158) + (form.description.length > 158 ? "…" : ""))}
              </div>
            </div>
            {preview && (
              <div className="mt-6 space-y-2 text-sm">
                <div className={`flex items-center justify-between p-2.5 rounded-md ${preview.title_ok ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <span>Title length</span>
                  <span className="tabular-nums font-medium">{preview.title_length} chars {preview.title_ok ? '✓' : '⚠'}</span>
                </div>
                <div className={`flex items-center justify-between p-2.5 rounded-md ${preview.description_ok ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <span>Description length</span>
                  <span className="tabular-nums font-medium">{preview.description_length} chars {preview.description_ok ? '✓' : '⚠'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
